<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script lang="ts" setup>
import type { Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoot } from '~/store'

const {
  animation = () => ({
    animated: false,
    animateValue: false,
    animationDuration: 2000,
    animationFunction: 'ease-in-out'
  }),
  value,
  max = 100,
  min = 0,
  stepSize = 1,
  disabled = false,
  readonly = false,
  size = 100,
  responsive = false,
  strokeWidth = 17
} = defineProps<{
  animation?: () => object
  value: number
  max?: number
  min?: number
  stepSize?: number
  disabled?: boolean
  readonly?: boolean
  size?: number
  responsive?: boolean
  strokeWidth?: number
}>()

const emits = defineEmits<{ (e: 'input', calc: number): void }>()

const { theme } = storeToRefs(useRoot())

const knob = ref<HTMLElement | null>(null)
const pathValue = ref<HTMLElement | null>(null)

const RADIUS = 40
const MID_X = 50
const MID_Y = 50
const MIN_RADIANS = (4 * Math.PI) / 3
const MAX_RADIANS = -Math.PI / 3

const _length = ref(0)
const animatedValue = ref(0)
const interval: Ref<any | null> = ref(null)

function mapRange(x: number, inMin: number, inMax: number, outMin: number, outMax: number) {
  return ((x - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin
}

const dashStyle = computed(() => {
  return {
    strokeDasharray: _length.value,
    strokeDashoffset: _length.value
  }
})

const style = computed(() => {
  return {
    height: responsive ? `${size}%` : `${size - 5}px`
  }
})

const computedSize = computed(() => {
  return responsive ? `${size}%` : size
})

const zeroRadians = computed(() => {
  /* this weird little bit of logic below is to handle the fact that usually we
                want the value arc to start drawing from the 'zero' point, but, in the case
                that the minimum and maximum values are both above zero, we set the 'zero point'
                at the supplied minimum, so the value arc renders as the user would expect */
  if (min > 0 && max > 0) return mapRange(min, min, max, MIN_RADIANS, MAX_RADIANS)
  else return mapRange(0, min, max, MIN_RADIANS, MAX_RADIANS)
})

const valueRadians = computed(() => {
  return mapRange(value, min, max, MIN_RADIANS, MAX_RADIANS)
})

const largeArc = computed(() => {
  return Math.abs(zeroRadians.value - valueRadians.value) < Math.PI ? 0 : 1
})

const sweep = computed(() => {
  return valueRadians.value > zeroRadians.value ? 0 : 1
})

const minX = computed(() => {
  return MID_X + Math.cos(MIN_RADIANS) * RADIUS
})

const minY = computed(() => {
  return MID_Y - Math.sin(MIN_RADIANS) * RADIUS
})

const maxX = computed(() => {
  return MID_X + Math.cos(MAX_RADIANS) * RADIUS
})

const maxY = computed(() => {
  return MID_Y - Math.sin(MAX_RADIANS) * RADIUS
})

const zeroX = computed(() => {
  return MID_X + Math.cos(zeroRadians.value) * RADIUS
})

const zeroY = computed(() => {
  return MID_Y - Math.sin(zeroRadians.value) * RADIUS
})

const valueX = computed(() => {
  return MID_X + Math.cos(valueRadians.value) * RADIUS
})

const valueY = computed(() => {
  return MID_Y - Math.sin(valueRadians.value) * RADIUS
})

const rangePath = computed(() => {
  return `M ${minX.value} ${minY.value} A ${RADIUS} ${RADIUS} 0 1 1 ${maxX.value} ${maxY.value}`
})

const valuePath = computed(() => {
  return `M ${zeroX.value} ${zeroY.value} A ${RADIUS} ${RADIUS} 0 ${largeArc.value} ${sweep.value} ${valueX.value} ${valueY.value}`
})

const showValue = computed(() => {
  return value >= min && value <= max && !disabled
})

const valueDisplay = computed(() => {
  // @ts-expect-error prop type object
  if (animation.animateValue) return animatedValue.value
  else return value
})

const primaryColor = computed(() => theme.value?.hexPrimary)
const secondaryColor = computed(() => theme.value?.hexSecondary)
const textColor = computed(() => theme.value?.hexPrimary)

function updatePosition(offsetX: number, offsetY: number) {
  const dx = offsetX - size / 2
  const dy = size / 2 - offsetY
  const angle = Math.atan2(dy, dx)
  let v
  /* bit of weird looking logic to map the angles returned by Math.atan2() onto
                our own unconventional coordinate system */
  const start = -Math.PI / 2 - Math.PI / 6
  if (angle > MAX_RADIANS) v = mapRange(angle, MIN_RADIANS, MAX_RADIANS, min, max)
  else if (angle < start) v = mapRange(angle + 2 * Math.PI, MIN_RADIANS, MAX_RADIANS, min, max)
  else return
  emits('input', Math.round((v - min) / stepSize) * stepSize + min)
}

function onClick(e: any) {
  if (!disabled && !readonly) updatePosition(e.offsetX, e.offsetY)
}

function onMouseDown(e: any) {
  if (!disabled && !readonly) {
    e.preventDefault()
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }
}

function onMouseUp(e: any) {
  if (!disabled && !readonly) {
    e.preventDefault()
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }
}

function onTouchStart(e: any) {
  if (!disabled && !readonly) {
    e.preventDefault()
    window.addEventListener('touchmove', onTouchMove)
    window.addEventListener('touchend', onTouchEnd)
  }
}

function onTouchEnd(e: any) {
  if (!disabled && !readonly) {
    e.preventDefault()
    window.removeEventListener('touchmove', onTouchMove)
    window.removeEventListener('touchend', onTouchEnd)
  }
}

function onMouseMove(e: any) {
  if (!disabled && !readonly) {
    e.preventDefault()
    updatePosition(e.offsetX, e.offsetY)
  }
}

function onTouchMove(e: any) {
  if (!disabled && readonly && e.touches.length === 1) {
    const boundingClientRect = knob.value?.getBoundingClientRect()
    if (boundingClientRect) {
      const touch = e.targetTouches.item(0)
      const offsetX = touch.clientX - boundingClientRect.left
      const offsetY = touch.clientY - boundingClientRect.top
      updatePosition(offsetX, offsetY)
    }
  }
}

function dashLength() {
  // @ts-expect-error not scoped
  const length = pathValue.value?.getTotalLength?.()
  // @ts-expect-error prop type object
  if (animation.animated) {
    // @ts-expect-error prop type object
    element.style.animationDuration = `${animation.animationDuration / 1000}s`
    // @ts-expect-error prop type object
    element.style.animationFunction = animation.animationFunction
  }
  if (pathValue.value) {
    pathValue.value.dataset.dash = length
    _length.value = length
  }
}

onMounted(() => {
  dashLength() // the element should be in the DOM
  if (interval.value !== null) clearInterval(interval.value)

  interval.value = null
  // @ts-expect-error object prop
  if (animation.animateValue) {
    interval.value = setInterval(
      () => {
        if (animatedValue.value < value) {
          animatedValue.value += 1
        } else {
          clearInterval(interval.value)
          interval.value = null
        }
      },
      // @ts-expect-error object prop
      (animation.animationDuration * 1000) / value / 1000
    )
  }
})
</script>

<template lang="pug">
ClientOnly
  .knob-control(ref='knob' :style='style')
    svg(:width='computedSize' :height='computedSize' viewbox='0 0 100 100' @click='onClick' @mousedown='onMouseDown' @mouseup='onMouseUp' @touchstart='onTouchStart' @touchend='onTouchEnd')
      path.knob-control__range(:d='rangePath' :stroke-width='strokeWidth' :stroke='secondaryColor')
      path.knob-control__value(v-if='showValue' ref='pathValue' :d='valuePath' :stroke-width='strokeWidth' :stroke='primaryColor' :data-dash='_length' :style='dashStyle')
      text.knob-control__text-display(v-if='showValue' :x='50' :y='57' text-anchor='middle' fill='#ffffff')
        | {{ valueDisplay }}
</template>

<style lang="sass" scoped>
@keyframes dash-frame
  100%
    stroke-dashoffset: 0

.knob-control
  @apply flex-shrink-0
  transition: all 2s
.knob-control__range
  fill: none
  transition: stroke .1s ease-in
.knob-control__value
  animation-name: dash-frame
  animation-fill-mode: forwards
  fill: none
.knob-control__text-display
  font-size: 1.3rem
  text-align: center
</style>
